import { useEffect, useState } from "react";
import api from "@/lib/api";
import KeyData from "@/components/KeyData";
import CelestialLoader from "@/components/CelestialLoader";
import { useLang } from "@/context/LanguageContext";
import { Flame } from "lucide-react";

export default function Progress() {
  const { t, lang } = useLang();
  const [d, setD] = useState(null);
  const [p, setP] = useState(null);
  useEffect(() => {
    api.get("/progress/overview").then(r => setD(r.data)).catch(() => {});
    api.get("/practices/stats").then(r => setP(r.data)).catch(() => {});
  }, []);
  if (!d) return <CelestialLoader />;

  const mp = d.mission_progress;
  const maxWeekly = Math.max(1, ...d.weekly_annals.map(w => w.count));
  const locale = lang === "hi" ? "hi-IN" : "en-IN";

  return (
    <div className="animate-gentle-rise">
      <h1 className="font-display text-[36px] leading-tight" data-testid="progress-title">{lang === "hi" ? "आपकी प्रगति" : "Your Progress"}</h1>
      <p className="text-body mt-2 leading-relaxed" style={{ color: "var(--vedari-ink-soft)" }}>
        <KeyData>{d.days_walked}</KeyData> {lang === "hi" ? "दिन चले" : "days walked"} · <KeyData>{d.completed_subtasks}</KeyData> {lang === "hi" ? "कार्य" : "tasks completed"} · <KeyData>{d.annals_count}</KeyData> {lang === "hi" ? "प्रविष्टियाँ" : "entries"}
      </p>

      <div className="ornate-divider" />

      {/* Mission progress */}
      {mp && (
        <section className="vedari-card mb-6" data-testid="progress-mission">
          <p className="text-caption uppercase tracking-wider" style={{ color: "var(--vedari-ink-soft)" }}>{lang === "hi" ? "मिशन" : "Mission"}</p>
          <h3 className="font-serif text-h2 mt-1">{mp.name}</h3>
          <div className="flex items-baseline gap-2 mt-3">
            <span className="font-display text-[36px]" style={{ color: "var(--vedari-gold)" }}>
              <KeyData>{mp.pct}</KeyData>%
            </span>
            <span className="text-body-sm" style={{ color: "var(--vedari-ink-soft)" }}>{lang === "hi" ? "पूर्ण" : "completed"}</span>
          </div>
          <div className="h-1.5 rounded-full mt-2 overflow-hidden" style={{ backgroundColor: "var(--vedari-divider)" }}>
            <div className="h-full" style={{ width: `${mp.pct}%`, backgroundColor: "var(--vedari-gold)" }} />
          </div>
          <p className="text-body-sm mt-3" style={{ color: "var(--vedari-ink-soft)" }}>
            <KeyData>{mp.completed}</KeyData> {lang === "hi" ? "में से" : "of"} <KeyData>{mp.total}</KeyData> {lang === "hi" ? "मील के पत्थर" : "milestones"} · <KeyData>{mp.duration_days}</KeyData> {lang === "hi" ? "दिन की यात्रा" : "day campaign"}
          </p>
        </section>
      )}

      {/* PRACTICES */}
      {p && (
        <section className="vedari-card mb-6" data-testid="progress-practices">
          <p className="text-caption uppercase tracking-wider" style={{ color: "var(--vedari-gold)" }}>{t("progress.practices")}</p>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <Stat label={t("progress.current_streak")} value={p.current_streak} icon={<Flame size={14} strokeWidth={1.75} style={{ color: "var(--vedari-gold)" }} />} />
            <Stat label={t("progress.longest_streak")} value={p.longest_streak} />
            <Stat label={t("progress.total_completed")} value={p.total_completed} />
          </div>
          {Object.keys(p.by_category || {}).length > 0 && (
            <div className="mt-5 pt-4" style={{ borderTop: "1px solid var(--vedari-divider)" }}>
              <p className="text-caption uppercase tracking-wider mb-3" style={{ color: "var(--vedari-ink-soft)" }}>{t("progress.by_category")}</p>
              <ul className="space-y-1.5">
                {Object.entries(p.by_category).map(([cat, n]) => (
                  <li key={cat} className="flex justify-between text-body-sm">
                    <span style={{ color: "var(--vedari-ink-soft)" }}>{t("progress.cat_" + cat) || cat}</span>
                    <span><KeyData>{n}</KeyData></span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {p.recent?.length > 0 && (
            <div className="mt-5 pt-4" style={{ borderTop: "1px solid var(--vedari-divider)" }}>
              <p className="text-caption uppercase tracking-wider mb-3" style={{ color: "var(--vedari-ink-soft)" }}>{t("progress.recent_practices")}</p>
              <ul className="space-y-2">
                {p.recent.map((r, i) => (
                  <li key={i} className="flex justify-between gap-3 text-body-sm">
                    <span className="truncate">{r.title}</span>
                    <span className="shrink-0" style={{ color: "var(--vedari-gold)" }}>
                      <KeyData>{new Date(r.date).toLocaleDateString(locale, { day: "numeric", month: "short" })}</KeyData>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* Weekly rhythm */}
      <section className="vedari-card mb-6" data-testid="progress-weekly">
        <p className="text-caption uppercase tracking-wider" style={{ color: "var(--vedari-ink-soft)" }}>{lang === "hi" ? "पिछले 12 सप्ताह" : "Last 12 weeks"}</p>
        <h3 className="font-serif text-h2 mt-1">{lang === "hi" ? "साप्ताहिक लय" : "Weekly rhythm"}</h3>
        <div className="flex items-end gap-1.5 mt-5 h-16">
          {d.weekly_annals.length === 0 ? (
            <p className="text-body-sm font-serif italic" style={{ color: "var(--vedari-ink-soft)" }}>{lang === "hi" ? "अभी कुछ नहीं।" : "Nothing here yet."}</p>
          ) : d.weekly_annals.map((w, i) => (
            <div key={i} className="flex-1 rounded-sm" style={{ backgroundColor: "var(--vedari-gold)", height: `${(w.count / maxWeekly) * 100}%`, minHeight: "4px", opacity: 0.4 + (w.count / maxWeekly) * 0.6 }} title={`${w.week}: ${w.count}`} />
          ))}
        </div>
      </section>

      {/* Priorities */}
      <section className="vedari-card mb-6" data-testid="progress-priorities">
        <p className="text-caption uppercase tracking-wider" style={{ color: "var(--vedari-ink-soft)" }}>{lang === "hi" ? "जिसके साथ चलते हैं" : "What you walk with"}</p>
        <h3 className="font-serif text-h2 mt-1">{lang === "hi" ? "प्राथमिकताएँ" : "Priorities"}</h3>
        <div className="flex flex-wrap gap-2 mt-4">
          {d.priorities.map(p => (
            <span key={p} className="px-3 py-1.5 rounded-full text-body-sm" style={{ backgroundColor: "rgba(184,149,74,0.10)", color: "var(--vedari-gold)", border: "1px solid rgba(184,149,74,0.3)" }}>
              {p.replace(/-/g, " ")}
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-1">
        {icon}
        <span className="font-display text-h2" style={{ color: "var(--vedari-gold)" }}>
          <KeyData>{value}</KeyData>
        </span>
      </div>
      <p className="text-[10px] uppercase tracking-wider mt-1 leading-tight" style={{ color: "var(--vedari-ink-soft)" }}>{label}</p>
    </div>
  );
}
